// App.js
import React from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import ScrollToTop from './components/ScrollToTop';
import Home from './pages/Home';
import TopMenu from './components/TopMenu';
import SignupPage from './pages/SignupPage';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import BillsPage from './pages/BillsPage';
import AccountDetailsPage from './pages/AccountDetailsPage';
import ProtectedRoute from './components/ProtectedRoute';
import AuthOnlyRoute from './components/AuthOnlyRoute';
import RequireAuth from './components/RequireAuth';
import OnboardingRoute from './components/OnboardingRoute';
import 'bootstrap/dist/css/bootstrap.min.css';
import { UserProvider } from './context/UserContext';
import BillDetailPage from './pages/BillDetailPage';
import BallotMeasureDetailPage from './pages/BallotMeasureDetailPage';
import AuthSlider from './components/AuthComponents/AuthSlider';
import SetupRepresentative from './pages/SetupRepresentative/SetupRepresentative';
import DonatePage from './pages/DonatePage';
import UpcomingFeatures from './pages/UpcomingFeatures';
import VerifyIdentityModalPage from './pages/VerifyIdentityModalPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import TermsOfServicePage from './pages/TermsOfServicePage';
import ChildSafetyStandardsPage from './pages/ChildSafetyStandardsPage';
import Footer from './components/Footer';
import CampaignLanding from './pages/CampaignSite';
import CampaignListPage from './pages/CampaignListPage';
import CampaignDetailPage from './pages/CampaignDetailPage';
import CreateCampaignPage from './pages/CreateCampaignPage';
import TakeActionCampaignPage from './pages/TakeActionCampaignPage';
import TakeActionCampaignDraftPreviewPage from './pages/TakeActionCampaignDraftPreviewPage';
import CandidateDetail from './pages/CandidateDetail';
import CongressMemberDetailPage from './pages/CongressMemberDetailPage';
import DiscoverPage from './pages/DiscoverPage';
import VerificationCompletePage from './pages/VerificationCompletePage';
import SupportPage from './pages/SupportPage';
import SupportDeleteAccountPage from './pages/SupportDeleteAccountPage';
import DeleteAccountPage from './pages/DeleteAccountPage';
import JoinPage from './pages/JoinPage';
import CivixAccountsRedirect from './pages/JoinPageSections/CivixAccountsRedirect';
import LegacyJoinRedirect from './pages/JoinPageSections/LegacyJoinRedirect';
import AccountTypeSelectionPage from './pages/AccountTypeSelectionPage';
import UsernameSelectionPage from './pages/UsernameSelectionPage';
import FinalSetupPage from './pages/FinalSetupPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import EventsPage from './pages/EventsPage';
import EventsMapPage from './pages/EventsMapPage';
import EventDetailPage from './pages/EventDetailPage';
import CreateEventPage from './pages/CreateEventPage';
import CommunityPage from './pages/CommunityPage';
import PostDetailPage from './pages/PostDetailPage';
import CommunityCreatePostPage from './pages/CommunityCreatePostPage';
import UserProfilePage from './pages/UserProfilePage';
import PublicElectionsPage from './pages/UpcomingElections/PublicElectionsPage';
import PublicStateElectionsPage from './pages/UpcomingElections/PublicStateElectionsPage';
import ErrorBoundary from './components/ErrorBoundary';
import { FONT_PRIMARY } from './theme/fonts';

const HIDE_NAV_ROUTES = ['/campaign-site-example', '/verification-complete']; 

function TopNavGate() {
  const { pathname } = useLocation();
  const hide = HIDE_NAV_ROUTES.some(p => pathname.startsWith(p));
  return hide ? null : <TopMenu />;
}

function FooterGate() {
  const { pathname } = useLocation();
  const hide = HIDE_NAV_ROUTES.some(p => pathname.startsWith(p));
  const isSignInScreen = pathname === '/login' || pathname === '/auth/login' || pathname.startsWith('/auth/login/');
  return hide ? null : <Footer showWhiteTopBorder={isSignInScreen} />;
}

const theme = createTheme({
  typography: {
    fontFamily: FONT_PRIMARY,
  },
  palette: {
    primary: {
      main: '#047857',
      dark: '#065f46',
      light: '#059669',
      contrastText: '#ffffff',
    },
    success: {
      main: '#047857',
      dark: '#065f46',
      light: '#059669',
      contrastText: '#ffffff',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        // Do not set backgroundColor/color on root — it forced every variant
        // (including outlined) to green, so local sx text colors produced
        // unreadable dark-on-green. Primary styling belongs on containedPrimary only.
        root: {
          textTransform: 'none',
          minHeight: '2.5rem',
        },
        containedPrimary: {
          background: 'var(--theme-gradient)',
          color: '#ffffff',
          boxShadow: 'none',
          '&:hover': {
            background: 'var(--theme-gradient)',
            opacity: 0.92,
            boxShadow: 'none',
            color: '#ffffff',
          },
          '&.Mui-disabled': {
            background: 'var(--theme-gradient)',
            color: '#ffffff',
            opacity: 0.85,
          },
        },
        outlined: {
          backgroundColor: 'transparent',
        },
        containedSecondary: {
          border: '1.7px solid var(--primary-color)',
          backgroundColor: 'transparent',
          color: 'var(--primary-color)',
          '&:hover': { backgroundColor: '#f1f1f1ff' },
        },
        containedTertiary: {
          border: '1.7px solid var(--secondary-color)',
          backgroundColor: 'transparent',
          color: 'var(--secondary-color)',
          '&:hover': { backgroundColor: '#f1f1f1ff' },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          '& .MuiDataGrid-toolbarContainer .MuiButton-root': { marginTop: 0 },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        filledSuccess: {
          background: 'var(--theme-gradient)',
          color: '#fff',
          fontWeight: 600,
          '& .MuiAlert-icon': { color: '#fff' },
        },
      },
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <UserProvider>
        <Router>
          <Box
            sx={{
              minHeight: '100vh',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              alignItems: 'stretch',
            }}
          >
            <TopNavGate /> 
            <ScrollToTop />
            <Box component="main" sx={{ flex: 1, width: '100%', minWidth: 0 }}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/feedback" element={<Navigate to="/#section4" replace />} />
                  <Route path="/profile/:userId" element={<UserProfilePage />} />
                  <Route path="/discover" element={<DiscoverPage />} />
                  <Route path="/candidates" element={<DiscoverPage />} />
                  <Route path="/candidates/:candidateId" element={<CandidateDetail />} />
                  <Route path="/congress/:bioguideId" element={<CongressMemberDetailPage />} /> 
                  <Route path="/signup" element={<SignupPage />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/auth/:mode" element={<AuthSlider />} />
                  <Route path="/account-type-selection" element={<RequireAuth><AccountTypeSelectionPage /></RequireAuth>} />
                  <Route
                    path="/username-selection"
                    element={
                      <OnboardingRoute requireAccountType>
                        <UsernameSelectionPage />
                      </OnboardingRoute>
                    }
                  />
                  <Route
                    path="/final-setup"
                    element={
                      <OnboardingRoute requireAccountType requireUsername>
                        <FinalSetupPage />
                      </OnboardingRoute>
                    }
                  />
                  <Route path="/verify-email" element={<RequireAuth><VerifyEmailPage /></RequireAuth>} />
                  <Route path="/setup-representative" element={<RequireAuth><SetupRepresentative /></RequireAuth>} />
                  <Route
                    path="/verify-identity"
                    element={
                      <ProtectedRoute>
                        <VerifyIdentityModalPage />
                      </ProtectedRoute>
                    }
                  />

                  {/* Features page (formerly /join) */}
                  <Route path="/features" element={<JoinPage />} />
                  <Route path="/join" element={<LegacyJoinRedirect />} />
                  <Route path="/civix-accounts" element={<CivixAccountsRedirect />} />
                  <Route path="/pricing" element={<Navigate to="/features#plans" replace />} />
                  <Route path="/donate" element={<DonatePage />} />
                  <Route path="/verification-complete" element={<VerificationCompletePage />} />
                  <Route path="/upcoming-features" element={<UpcomingFeatures />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/child-safety-standards" element={<ChildSafetyStandardsPage />} />
                  <Route path="/support" element={<SupportPage />} />
                  <Route path="/support/delete-account" element={<SupportDeleteAccountPage />} />
                  <Route
                    path="/account/delete"
                    element={
                      <AuthOnlyRoute>
                        <DeleteAccountPage />
                      </AuthOnlyRoute>
                    }
                  />
                  <Route path="/campaign-site-example" element={<CampaignLanding />} />
                  <Route
                    path="/campaigns"
                    element={
                      <ProtectedRoute>
                        <CampaignListPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/campaigns/create"
                    element={
                      <ProtectedRoute>
                        <CreateCampaignPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/campaigns/:campaignId"
                    element={
                      <ProtectedRoute>
                        <CampaignDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/campaigns/preview/take-action"
                    element={
                      <ProtectedRoute>
                        <TakeActionCampaignDraftPreviewPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/campaigns/:campaignId/edit"
                    element={
                      <ProtectedRoute>
                        <CreateCampaignPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="/elections" element={<PublicElectionsPage />} />
                  <Route path="/elections/:stateSlug" element={<PublicStateElectionsPage />} />

                  <Route path="/bills" element={<BillsPage />} />
                  <Route path="/bills/ballot/:stateCode/:measureId" element={<BallotMeasureDetailPage />} />
                  <Route path="/bills/state/:stateCode/:identifier" element={<BillDetailPage />} />
                  <Route path="/bills/:id" element={<BillDetailPage />} />
                  <Route path="/take-action/:campaignId" element={<TakeActionCampaignPage />} />

                  <Route path="/community" element={<CommunityPage />} />
                  <Route path="/community/post/:postId" element={<PostDetailPage />} />
                  <Route
                    path="/community/create"
                    element={
                      <ProtectedRoute>
                        <CommunityCreatePostPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route path="/events" element={<EventsPage />} />
                  <Route path="/events/map" element={<EventsMapPage />} />
                  <Route path="/events/:eventId" element={<EventDetailPage />} />
                  <Route
                    path="/events/create"
                    element={
                      <ProtectedRoute>
                        <CreateEventPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/events/:eventId/edit"
                    element={
                      <ProtectedRoute>
                        <CreateEventPage />
                      </ProtectedRoute>
                    }
                  />

                  <Route
                    path="/dashboard"
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="/account-details"
                    element={
                      <ProtectedRoute>
                        <AccountDetailsPage />
                      </ProtectedRoute>
                    }
                  />
                </Routes>
            </Box>
            <FooterGate />
          </Box>
        </Router>
      </UserProvider>
    </ThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
